/**
 * DocumentTypesTab — F20 T20.17 (Settings → Typy dokumentów).
 *
 * Zarządzanie słownikiem typów dokumentów per klasa:
 *  - 7-klasowy segmented control (purchase_invoice / sale_invoice / contract / letter / commercial / internal / specjalne)
 *  - Toggle „pokaż skasowane" → GET ?include_deleted=true (wymaga uprawnienia manage)
 *  - Tabela typów dla wybranej klasy (nazwa, kod, format numeracji, kolejność, aktywny)
 *  - Per-wiersz: Edytuj, Usuń (ConfirmDialog danger), Przywróć (ConfirmDialog primary)
 *  - Przycisk „Dodaj typ" (manage-gated) → modal create
 *  - Bramka modułowa per klasa: 404 na GET → empty-state, nie error toast
 *  - Kolumna „Wniosek o nieobecność" (pole wyboru w wierszu) — ustawienie całego klienta, tylko na
 *    zakładkach klas HR (specjalne / wewnętrzne) i tylko dla super-usera albo osoby z prawem do
 *    słownika typów obu klas (`documents.specjalne|internal.document_types.view|manage`); logika
 *    zapisu w `useHrAbsenceTypeSetting`. Typ globalny jest zaznaczony we wszystkich wierszach firm
 *    (rozwiniętych pod tym samym `id`).
 *  - Kolumna „Pola typu” — podsumowanie (liczba pól, wymaganych, kropka przy odstępstwach od
 *    ustawienia ITE) z jednego zapytania zbiorczego; kliknięcie otwiera panel boczny
 *    `DocumentTypeFieldsDrawer` (pola i wymagalność per typ z nakładką firmy).
 *  - Edycja inline (Nazwa/Kod/Kolejność/Format numeracji/Aktywny) TYLKO dla wierszy
 *    lokalnych (origin='local', nie skasowanych, manage-gated) — lustro edytowalnej-inline
 *    tabeli typów globalnych z panelu ITE (`AdminGlobalDocumentTypes.tsx`); wiersze
 *    globalne pozostają w pełni read-only (akcje kopiuj/przypisz proces/przypisz
 *    layout/aktywuj-dezaktywuj bez zmian). Filtr klienta (Kod/Nazwa) nad tabelą.
 *
 * Namespace uprawnień per klasa (Etap C — documents.<klasa>):
 *   purchase_invoice → documents.purchase_invoice · sale_invoice → documents.sale_invoice ·
 *   contract → documents.contract · letter → documents.letter
 *   commercial → documents.commercial · internal → documents.internal
 *
 * Uprawnienia per klasa:
 *   view:   documents.<klasa>.document_types.view
 *   manage: documents.<klasa>.document_types.manage
 *
 * Konwencja UI (CLAUDE.md §4.12): ZERO window.confirm/alert.
 */

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DocumentClass, DocumentTypeRecord, MergedDocumentTypeRow } from '@saas/shared';
import {
  HR_ABSENCE_DOCUMENT_CLASSES,
  isHrAbsenceDocumentClass,
  normalizeNumberingBaseSlug,
} from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Copy,
  Eye,
  EyeOff,
  LayoutTemplate,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
  Workflow,
} from 'lucide-react';

import { InlineEditableCell } from '@/components/additional/InlineEditableCell';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Checkbox } from '@/components/Checkbox';
import { ConfigurableTable } from '@/components/configurable-table';
import type { ColumnDefinition } from '@/components/configurable-table/types';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { AssignLayoutModal } from '@/components/documents/AssignLayoutModal';
import { AssignProcessModal } from '@/components/documents/AssignProcessModal';
import { NumberingCell, type NumberingCellValue } from '@/components/documents/NumberingCell';
import { OriginBadge } from '@/components/documents/OriginBadge';
import { useNumberingBaseFieldOptions } from '@/components/documents/useNumberingBaseFieldOptions';
import { defaultProcessColumn, formLayoutColumn } from '@/components/kartoteki/recordTypeColumns';
import { Skeleton } from '@/components/Skeleton';
import { Spinner } from '@/components/Spinner';
import { Tabs } from '@/components/Tabs';
import { Tooltip } from '@/components/Tooltip';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import {
  documentTypeFieldsSummaryKey,
  fetchDocumentTypeFieldsSummary,
  summaryPairKey,
} from '@/lib/documents/document-type-fields';
import { HR_ABSENCE_TYPE_DEPENDENT_QUERY_KEYS } from '@/lib/hr/query-keys';
import { DOCUMENTS_MODULE_CODE } from '@/lib/processes/module-routes';
import {
  defaultProcessErrorMessage,
  handleDefaultProcessErrorCode,
  useDefaultProcessOptions,
} from '@/lib/record-types/default-process';
import { type FormLayoutOption, handleFormLayoutErrorCode } from '@/lib/record-types/form-layout';
import { companyIdParam, useActiveCompanyScope } from '@/lib/use-active-company';
import { useCompanies } from '@/lib/use-permissions';

import { DocumentTypeFieldsDrawer } from './DocumentTypeFieldsDrawer';
import { DocumentTypeFormModal, type DocumentTypeFormValues } from './DocumentTypeFormModal';
import { useHrAbsenceTypeSetting } from './useHrAbsenceTypeSetting';

// ─── Typy ───────────────────────────────────────────────────────────────────

/** Namespace uprawnień słownika typów per klasa (Etap C: documents.<klasa>.document_types.*). */
const CLASS_PERM_NS: Record<DocumentClass, string> = {
  // Każda klasa faktur ma WŁASNY namespace uprawnień (F24) — wspólny `documents.invoice`
  // został wycofany, więc nikt go już nie ma; zostawienie go tutaj gasiłoby całą zakładkę.
  purchase_invoice: 'documents.purchase_invoice',
  sale_invoice: 'documents.sale_invoice',
  contract: 'documents.contract',
  letter: 'documents.letter',
  commercial: 'documents.commercial',
  internal: 'documents.internal',
  specjalne: 'documents.specjalne',
};

const ALL_CLASSES: DocumentClass[] = [
  'purchase_invoice',
  'sale_invoice',
  'contract',
  'letter',
  'commercial',
  'internal',
  'specjalne',
];

/**
 * Wiersz tabeli typów dokumentów w ustawieniach tenanta — scalona lista
 * (`MergedDocumentTypeRow` z `@saas/shared`, obejmuje flagi origin/status/shadowed/
 * deactivatedInCompany/selectable/readonly oraz numberingBaseField/sortOrder/gcrecord).
 */
type DocumentType = MergedDocumentTypeRow;

// ─── Helpers ────────────────────────────────────────────────────────────────

function viewPerm(docClass: DocumentClass): string {
  return `${CLASS_PERM_NS[docClass]}.document_types.view`;
}

function managePerm(docClass: DocumentClass): string {
  return `${CLASS_PERM_NS[docClass]}.document_types.manage`;
}

function emptyFormValues(): DocumentTypeFormValues {
  return {
    code: '',
    name: '',
    numberingFormat: '',
    sortOrder: '',
    isActive: true,
    defaultProcessId: '',
    numberingBaseField: 'document_date',
    numberingReset: 'yearly',
    companyId: null,
    description: '',
    formLayoutId: null,
  };
}

function typeToFormValues(dt: DocumentType): DocumentTypeFormValues {
  return {
    code: dt.code,
    name: dt.name,
    numberingFormat: dt.numberingFormat ?? '',
    sortOrder: String(dt.sortOrder),
    isActive: dt.isActive,
    defaultProcessId: dt.defaultProcessId ?? '',
    numberingBaseField: dt.numberingBaseField ?? 'document_date',
    numberingReset: dt.numberingReset ?? 'yearly',
    companyId: dt.companyId,
    description: dt.description ?? '',
    formLayoutId: dt.formLayoutId,
  };
}

/**
 * Prefill dla modala „Utwórz kopię lokalną" (mode='copy') — startuje z danymi
 * globalnego wiersza. `defaultProcessId` świadomie NIE jest kopiowany (globalne
 * typy go nie mają — patrz komentarz w backendowym COPY endpointzie); firma jest
 * stała na `row.companyId` (Model B, materializacja per firma). `formLayoutId`
 * (T1.8) analogicznie NIE jest kopiowany — typ globalny nie zna layoutów tenanta
 * (wskazanie layoutu globalnego przyjdzie dopiero w Etapie 3).
 */
function typeToCopyFormValues(dt: DocumentType): DocumentTypeFormValues {
  return {
    code: dt.code,
    name: dt.name,
    numberingFormat: dt.numberingFormat ?? '',
    sortOrder: '',
    isActive: true,
    defaultProcessId: '',
    numberingBaseField: dt.numberingBaseField ?? 'document_date',
    numberingReset: dt.numberingReset ?? 'yearly',
    companyId: dt.companyId,
    description: dt.description ?? '',
    formLayoutId: null,
  };
}

// ─── Główny komponent ────────────────────────────────────────────────────────

export function DocumentTypesTab() {
  const { t } = useTranslation();
  const { me } = useTenantAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  // Start na PIERWSZEJ zakładce paska, do której user ma prawo (podgląd lub zarządzanie) — nie na
  // twardo wybranej klasie: dawne 'contract' otwierało „Umowy", choć pierwsze na pasku są faktury.
  // Klasa startowa liczona na bieżąco z praw (nie raz w inicjalizatorze stanu): przy odświeżeniu
  // strony prawa mogą dojść po pierwszym renderze. W stanie trzymamy tylko wybór użytkownika.
  const firstAllowedClass = useMemo<DocumentClass>(() => {
    if (me?.user.isSuperUser === true) return ALL_CLASSES[0]!;
    return (
      ALL_CLASSES.find(
        (cls) =>
          (me?.permissions.includes(viewPerm(cls)) ?? false) ||
          (me?.permissions.includes(managePerm(cls)) ?? false),
      ) ?? ALL_CLASSES[0]!
    );
  }, [me]);
  const [pickedClass, setSelectedClass] = useState<DocumentClass | null>(null);
  const selectedClass = pickedClass ?? firstAllowedClass;
  const [showDeleted, setShowDeleted] = useState(false);

  // Opcje pola daty numeracji dla aktywnej klasy: core (stała) + pola dodatkowe (ext)
  // tenanta. Liczone RAZ tutaj i współdzielone przez wszystkie komórki NumberingCell
  // oraz oba modale typu (zamiast hooka per wiersz).
  const numberingBaseFieldOptions = useNumberingBaseFieldOptions(selectedClass, true);

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<DocumentType | null>(null);
  const [pendingDelete, setPendingDelete] = useState<DocumentType | null>(null);
  const [pendingRestore, setPendingRestore] = useState<DocumentType | null>(null);

  // Etap 4 — akcje tenanta na wierszach globalnych (origin === 'global').
  const [copyingFrom, setCopyingFrom] = useState<DocumentType | null>(null);
  const [pendingDeactivate, setPendingDeactivate] = useState<DocumentType | null>(null);
  const [assigningProcessTo, setAssigningProcessTo] = useState<DocumentType | null>(null);
  const [assigningLayoutTo, setAssigningLayoutTo] = useState<DocumentType | null>(null);
  // Panel „Pola typu” — otwierany kliknięciem w komórkę kolumny „Pola typu”.
  const [fieldsPanelFor, setFieldsPanelFor] = useState<DocumentType | null>(null);

  // `initialValues` DLA modali create/edit/copy — MEMOIZOWANE (code-review debt, obszar
  // numeracji, F20). Budowanie inline w JSX (`editing ? typeToFormValues(editing) : ...`)
  // tworzyło NOWY obiekt przy KAŻDYM renderze tej zakładki, nie tylko gdy `editing`/
  // `copyingFrom` faktycznie się zmieniały. `DocumentTypeFormModal` resetuje swój stan
  // formularza w efekcie zależnym od `initialValues` (poprawne, pełne deps) — niestabilna
  // referencja odpalałaby ten reset przy KAŻDYM nieistotnym re-renderze zakładki (np.
  // odświeżeniu `numberingBaseFieldOptions`), wycierając niezapisaną edycję w trakcie
  // pisania. `editing`/`copyingFrom` to stabilne referencje stanu — zmieniają się TYLKO
  // przy otwarciu/zamknięciu modala na konkretnym wierszu (`setEditing`/`setCopyingFrom`
  // wołane wyłącznie tam) — więc memoizacja keyowana nimi naprawia problem bez zmiany
  // kontraktu modala.
  const editModalInitialValues = useMemo(
    () => (editing ? typeToFormValues(editing) : emptyFormValues()),
    [editing],
  );
  const copyModalInitialValues = useMemo(
    () => (copyingFrom ? typeToCopyFormValues(copyingFrom) : emptyFormValues()),
    [copyingFrom],
  );

  const isSuperUser = me?.user.isSuperUser === true;
  const canView = isSuperUser || (me?.permissions.includes(viewPerm(selectedClass)) ?? false);
  const canManage = isSuperUser || (me?.permissions.includes(managePerm(selectedClass)) ?? false);
  // Kolumna „Wniosek o nieobecność" (ustawienie całego klienta) — widoczna dla super-usera
  // albo osoby z prawem do słownika typów (podgląd lub zarządzanie) OBU klas, z których można
  // wybrać typ. To tylko podpowiedź w panelu — o dostępie rozstrzyga serwer (403).
  const canSeeHrAbsenceType =
    isSuperUser ||
    HR_ABSENCE_DOCUMENT_CLASSES.every(
      (cls) =>
        (me?.permissions.includes(viewPerm(cls)) ?? false) ||
        (me?.permissions.includes(managePerm(cls)) ?? false),
    );
  // Kolumna istnieje tylko na zakładkach klas HR; ustawienie czytamy wyłącznie wtedy.
  const showHrAbsenceColumn = canSeeHrAbsenceType && isHrAbsenceDocumentClass(selectedClass);
  const hrAbsence = useHrAbsenceTypeSetting(showHrAbsenceColumn);

  // T20D — firmy tenanta: kolumna „Firma” w tabeli + picker w modalu create.
  // useMemo: referencyjnie stabilne między renderami (dep `companyLabel`/useCallback niżej).
  const companiesQ = useCompanies();
  const companies = useMemo(() => companiesQ.data?.companies ?? [], [companiesQ.data]);

  // C2 — firma aktywna z top-baru (tryb "Wszystkie firmy" = undefined, jak dotąd).
  const companyScope = useActiveCompanyScope();
  const scopedCompanyId = companyIdParam(companyScope);

  // useCallback: referencyjnie stabilna między renderami (o ile companies/t się nie
  // zmieniły) — zasila też kolumnę „Firma" tabeli (wyświetlanie + filtr po nazwie firmy).
  const companyLabel = useCallback(
    (companyId: string | null): string => {
      if (!companyId) return t('common.notSet');
      return companies.find((c) => c.id === companyId)?.legalName ?? t('common.notSet');
    },
    [companies, t],
  );

  // Query key uwzględnia klasę, tryb skasowanych i firmę aktywną z top-baru (C2)
  const queryKey = [
    'document-types',
    selectedClass,
    { includeDeleted: showDeleted, companyId: scopedCompanyId ?? null },
  ] as const;

  const listQ = useQuery({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams({ class: selectedClass });
      if (showDeleted && canManage) params.set('include_deleted', 'true');
      if (scopedCompanyId) params.set('companyId', scopedCompanyId);
      const res = await api.get<{ documentTypes: DocumentType[] }>(
        `/document-types?${params.toString()}`,
      );
      return res.data.documentTypes;
    },
    // Nie rzucaj errorem dla 404 (moduł nieaktywny) — obsługujemy w render
    retry: false,
    // Nie wysyłaj doomed 403 gdy user nie ma żadnego uprawnienia do tej klasy
    enabled: canView || canManage,
  });

  // Podsumowanie pól typów (kolumna „Pola typu”) — jedno zapytanie zbiorcze na klasę, nie jedno
  // na wiersz. Błąd odczytu nie blokuje tabeli: komórka pokazuje wtedy „Ustaw pola” bez liczb.
  const summaryQ = useQuery({
    queryKey: documentTypeFieldsSummaryKey(selectedClass, scopedCompanyId ?? null),
    queryFn: () => fetchDocumentTypeFieldsSummary(selectedClass, scopedCompanyId ?? null),
    enabled: canView || canManage,
    retry: false,
    staleTime: 30_000,
  });
  const summaryByPair = useMemo(
    () =>
      new Map(
        (summaryQ.data ?? []).map((s) => [summaryPairKey(s.typeId, s.companyId), s] as const),
      ),
    [summaryQ.data],
  );

  // T20D.6 — opublikowane procesy celujące w dokumenty (picker domyślnego procesu).
  // Filtr targetModuleCode jest WYMOGIEM poprawności: backend (T20D.4) odrzuca 400
  // proces o innym celu, więc nie pokazujemy opcji, której zapis by nie przyjął.
  // enabled: canView TEŻ (nie tylko canManage) — kolumna „Proces domyślny" w tabeli
  // pokazuje resolved nazwę procesu również read-only widzowi, nie tylko w pickerze.
  const { processes: processOptions, loading: processesLoading } = useDefaultProcessOptions(
    DOCUMENTS_MODULE_CODE,
    canView || canManage,
  );

  // T1.8 — layouty FORMULARZA kartoteki wybranej klasy, do pickera „Layout formularza"
  // w DocumentTypeFormModal ORAZ do nowej kolumny inline „Layout formularza" w tabeli
  // (wyświetlanie read-only + `LayoutPickerCell`) — jedno zapytanie React Query
  // współdzielone przez modale i tabelę. enabled ilekroć zakładka klasy jest widoczna
  // (nie tylko gdy jakiś modal jest otwarty — kolumna w tabeli potrzebuje danych zawsze).
  // Serwer wpuszcza view ALBO manage — widz bez manage też widzi nazwę układu w kolumnie.
  const layoutsQ = useQuery({
    queryKey: ['document-types', 'form-layouts', selectedClass],
    queryFn: async () => {
      const res = await api.get<{ layouts: FormLayoutOption[] }>(
        `/document-types/form-layouts?class=${selectedClass}`,
      );
      return res.data.layouts;
    },
    enabled: (canView || canManage) && Boolean(selectedClass),
    retry: false,
  });
  const layoutOptions = layoutsQ.data ?? [];
  const layoutsUnavailable = layoutsQ.isError;

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['document-types', selectedClass] });
    // Zmiana przypisania layoutu do typu musi unieważnić cache layoutu aktywnego
    // (`system-module-layout-active` — jedyny klucz, także dla zakładek „Dane"). Bez suffixu (prefix match łapie
    // wszystkie zamontowane warianty moduł/typ) — świadomie szeroko: przy kartach
    // keep-alive (ADR-0051) refetch trafi też layouty innych otwartych kart, ale
    // każda mutacja typu może zmienić efektywne rozstrzyganie (przesłanianie
    // globalnych, kopia z formLayoutId, dezaktywacja), a to rzadka akcja w Ustawieniach.
    queryClient.invalidateQueries({ queryKey: ['system-module-layout-active'] });
    // Typy klas specjalne/internal są kandydatami na typ wniosku o nieobecność — ich zmiana
    // (dodanie, usunięcie, kopia) może zmienić listę wyboru, obowiązujący typ, skrót
    // „Nieobecności" i kafelek „Urlopy". Odświeżamy więc też te zapytania.
    if (isHrAbsenceDocumentClass(selectedClass)) {
      for (const queryKey of HR_ABSENCE_TYPE_DEPENDENT_QUERY_KEYS) {
        queryClient.invalidateQueries({ queryKey });
      }
    }
  }

  // Buduje payload PUT z tylko zmienionych pól.
  // companyId NIE jest tu diffowane — backend PUT nie ma tego pola w schemacie
  // (firma jest ustawiana raz przy tworzeniu i jest immutable po utworzeniu typu).
  function buildPutPayload(
    values: DocumentTypeFormValues,
    original: DocumentType,
  ): Record<string, unknown> {
    const payload: Record<string, unknown> = {};
    if (values.code !== original.code) payload.code = values.code;
    if (values.name !== original.name) payload.name = values.name;
    const desc = values.description.trim() || null;
    if (desc !== original.description) payload.description = desc;
    const fmt = values.numberingFormat.trim() || null;
    if (fmt !== original.numberingFormat) payload.numberingFormat = fmt;
    const sort = values.sortOrder.trim() !== '' ? parseInt(values.sortOrder, 10) : null;
    if (sort !== original.sortOrder) payload.sortOrder = sort;
    if (values.isActive !== original.isActive) payload.isActive = values.isActive;
    // T20D.6 — domyślny proces: '' (brak) → null; diff względem oryginału
    const proc = values.defaultProcessId.trim() || null;
    if (proc !== original.defaultProcessId) payload.defaultProcessId = proc;
    // Pola numeracji — porównujemy ze stanem bazy (null → fallback 'document_date'/'yearly')
    const baseField = values.numberingBaseField || 'document_date';
    const origBaseField = original.numberingBaseField ?? 'document_date';
    if (baseField !== origBaseField) payload.numberingBaseField = baseField;
    if (values.numberingReset !== (original.numberingReset ?? 'yearly')) {
      payload.numberingReset = values.numberingReset;
    }
    // T1.8 — layout formularza: null = kaskada domyślna kartoteki (wyczyszczenie wskazania).
    if (values.formLayoutId !== (original.formLayoutId ?? null)) {
      payload.formLayoutId = values.formLayoutId;
    }
    return payload;
  }

  const createMutation = useMutation({
    mutationFn: async (values: DocumentTypeFormValues) => {
      const body: Record<string, unknown> = {
        class: selectedClass,
        code: values.code,
        name: values.name,
        // companyId jest WYMAGANE przez backend przy tworzeniu (Model B, T20D) — modal
        // waliduje jego obecność w mode='create', więc tutaj po walidacji jest zawsze ustawione.
        companyId: values.companyId,
      };
      if (values.description.trim()) body.description = values.description.trim();
      if (values.numberingFormat.trim()) body.numberingFormat = values.numberingFormat.trim();
      if (values.sortOrder.trim() !== '') body.sortOrder = parseInt(values.sortOrder, 10);
      body.isActive = values.isActive;
      // T20D.6 — domyślny proces (gdy wybrany); '' = brak → pomijamy
      if (values.defaultProcessId.trim()) body.defaultProcessId = values.defaultProcessId.trim();
      // Pola numeracji — zawsze wysyłamy (backend ma własne defaulty, ale UI też ma)
      if (values.numberingBaseField) body.numberingBaseField = values.numberingBaseField;
      body.numberingReset = values.numberingReset;
      // T1.8 — layout formularza (null = kaskada domyślna kartoteki); zawsze wysyłamy
      // jawnie, tak jak isActive (backend przyjmuje string|null|undefined).
      body.formLayoutId = values.formLayoutId;
      const res = await api.post<{ documentType: DocumentTypeRecord }>('/document-types', body);
      return res.data.documentType;
    },
    onSuccess: () => {
      invalidate();
      toast.show({ variant: 'success', title: t('tenant.documentTypes.toast.created') });
      setCreateOpen(false);
    },
    onError: (err) => {
      const code = isAxiosError(err) ? (err.response?.data?.error as string | undefined) : null;
      if (code === 'CODE_TAKEN') {
        toast.show({ variant: 'warning', title: t('tenant.documentTypes.toast.codeTaken') });
        return;
      }
      if (code === 'NAME_TAKEN') {
        toast.show({ variant: 'warning', title: t('tenant.documentTypes.toast.nameTaken') });
        return;
      }
      if (code === 'INVALID_NUMBERING_FORMAT' || code === 'NUMBERING_FORMAT_NO_COUNTER') {
        toast.show({
          variant: 'warning',
          title: t('tenant.documentTypes.toast.numberingFormatInvalid'),
        });
        return;
      }
      // T1.8 — kody walidatora layoutu formularza (`lib/record-types/validate-form-layout.ts`), mapowanie
      // współdzielone przez create/update/copy (patrz `handleFormLayoutErrorCode`).
      if (handleFormLayoutErrorCode(code, toast, t)) return;
      if (handleDefaultProcessErrorCode(code, toast, t)) return;
      toast.show({ variant: 'error', title: t('tenant.documentTypes.toast.createError') });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      values,
      original,
    }: {
      id: string;
      values: DocumentTypeFormValues;
      original: DocumentType;
    }) => {
      const payload = buildPutPayload(values, original);
      const res = await api.put<{ documentType: DocumentTypeRecord }>(
        `/document-types/${id}`,
        payload,
      );
      return res.data.documentType;
    },
    onSuccess: () => {
      invalidate();
      toast.show({ variant: 'success', title: t('tenant.documentTypes.toast.updated') });
      setEditing(null);
    },
    onError: (err) => {
      const code = isAxiosError(err) ? (err.response?.data?.error as string | undefined) : null;
      if (code === 'CODE_TAKEN') {
        toast.show({ variant: 'warning', title: t('tenant.documentTypes.toast.codeTaken') });
        return;
      }
      if (code === 'NAME_TAKEN') {
        toast.show({ variant: 'warning', title: t('tenant.documentTypes.toast.nameTaken') });
        return;
      }
      if (code === 'INVALID_NUMBERING_FORMAT' || code === 'NUMBERING_FORMAT_NO_COUNTER') {
        toast.show({
          variant: 'warning',
          title: t('tenant.documentTypes.toast.numberingFormatInvalid'),
        });
        return;
      }
      // T1.8 — kody walidatora layoutu formularza (`lib/record-types/validate-form-layout.ts`), mapowanie
      // współdzielone przez create/update/copy (patrz `handleFormLayoutErrorCode`).
      if (handleFormLayoutErrorCode(code, toast, t)) return;
      if (handleDefaultProcessErrorCode(code, toast, t)) return;
      toast.show({ variant: 'error', title: t('tenant.documentTypes.toast.updateError') });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/document-types/${id}`);
    },
    onSuccess: () => {
      invalidate();
      toast.show({ variant: 'success', title: t('tenant.documentTypes.toast.deleted') });
      setPendingDelete(null);
    },
    onError: (err) => {
      const code = isAxiosError(err) ? (err.response?.data?.error as string | undefined) : null;
      if (code === 'TYPE_IN_USE') {
        toast.show({ variant: 'warning', title: t('tenant.documentTypes.toast.typeInUse') });
        setPendingDelete(null);
        return;
      }
      toast.show({ variant: 'error', title: t('tenant.documentTypes.toast.deleteError') });
      setPendingDelete(null);
    },
  });

  const restoreMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/document-types/${id}/restore`);
    },
    onSuccess: () => {
      invalidate();
      toast.show({ variant: 'success', title: t('tenant.documentTypes.toast.restored') });
      setPendingRestore(null);
    },
    onError: (err) => {
      const code = isAxiosError(err) ? (err.response?.data?.error as string | undefined) : null;
      if (code === 'CODE_TAKEN') {
        toast.show({ variant: 'warning', title: t('tenant.documentTypes.toast.codeTaken') });
        setPendingRestore(null);
        return;
      }
      if (code === 'NAME_TAKEN') {
        toast.show({ variant: 'warning', title: t('tenant.documentTypes.toast.nameTaken') });
        setPendingRestore(null);
        return;
      }
      if (code === 'NOT_DELETED') {
        toast.show({ variant: 'warning', title: t('tenant.documentTypes.toast.notDeleted') });
        setPendingRestore(null);
        return;
      }
      toast.show({ variant: 'error', title: t('tenant.documentTypes.toast.restoreError') });
      setPendingRestore(null);
    },
  });

  // Etap 4 — „Utwórz kopię lokalną" (typ globalny → wiersz lokalny w tej samej firmie).
  const copyMutation = useMutation({
    mutationFn: async ({
      globalId,
      values,
    }: {
      globalId: string;
      values: DocumentTypeFormValues;
    }) => {
      const body: Record<string, unknown> = {
        companyId: values.companyId,
        name: values.name,
        code: values.code,
      };
      if (values.description.trim()) body.description = values.description.trim();
      if (values.numberingFormat.trim()) body.numberingFormat = values.numberingFormat.trim();
      if (values.sortOrder.trim() !== '') body.sortOrder = parseInt(values.sortOrder, 10);
      body.isActive = values.isActive;
      if (values.numberingBaseField) body.numberingBaseField = values.numberingBaseField;
      body.numberingReset = values.numberingReset;
      // T1.8 — layout formularza; w mode='copy' modal startuje zawsze z null (patrz
      // typeToCopyFormValues), ale przekazujemy jawnie żeby user mógł go tu wybrać.
      body.formLayoutId = values.formLayoutId;
      const res = await api.post<{ documentType: DocumentTypeRecord }>(
        `/document-types/global/${globalId}/copy`,
        body,
      );
      return res.data.documentType;
    },
    onSuccess: () => {
      invalidate();
      toast.show({
        variant: 'success',
        title: t('tenant.documentTypes.toast.copied'),
        description: t('tenant.documentTypes.toast.copyHint'),
      });
      setCopyingFrom(null);
    },
    onError: (err) => {
      const code = isAxiosError(err) ? (err.response?.data?.error as string | undefined) : null;
      if (code === 'CODE_TAKEN') {
        toast.show({ variant: 'warning', title: t('tenant.documentTypes.toast.codeTaken') });
        return;
      }
      if (code === 'NAME_TAKEN') {
        toast.show({ variant: 'warning', title: t('tenant.documentTypes.toast.nameTaken') });
        return;
      }
      if (code === 'INVALID_NUMBERING_FORMAT' || code === 'NUMBERING_FORMAT_NO_COUNTER') {
        toast.show({
          variant: 'warning',
          title: t('tenant.documentTypes.toast.numberingFormatInvalid'),
        });
        return;
      }
      // T1.8 — kody walidatora layoutu formularza (`lib/record-types/validate-form-layout.ts`), mapowanie
      // współdzielone przez create/update/copy (patrz `handleFormLayoutErrorCode`).
      if (handleFormLayoutErrorCode(code, toast, t)) return;
      if (handleDefaultProcessErrorCode(code, toast, t)) return;
      if (code === 'GLOBAL_TYPE_NOT_PUBLISHED') {
        toast.show({
          variant: 'warning',
          title: t('tenant.documentTypes.toast.globalTypeNotPublished'),
        });
        return;
      }
      toast.show({ variant: 'error', title: t('tenant.documentTypes.toast.copyError') });
    },
  });

  // Etap 4 — nadpisanie tenantowe typu globalnego (deaktywacja w firmie / domyślny proces).
  // PUT z pełnym zamierzonym stanem (isDeactivated + defaultProcessId razem, gdy oba znane) —
  // upsert na backendzie aktualizuje tylko przekazane pola, ale wysyłamy komplet żeby jedna
  // akcja (np. „Aktywuj”) nie nadpisała przypadkiem drugiego pola przy równoległym użyciu.
  const overrideMutation = useMutation({
    mutationFn: async ({
      globalId,
      companyId,
      isDeactivated,
      defaultProcessId,
      formLayoutId,
    }: {
      globalId: string;
      companyId: string;
      isDeactivated?: boolean;
      defaultProcessId?: string | null;
      // T2.3 — layout formularza przypisany do typu globalnego w tej firmie (nadpisanie
      // tenantowe, `document_type_overrides.form_layout_id`, wzorzec `defaultProcessId`).
      formLayoutId?: string | null;
    }) => {
      const body: Record<string, unknown> = { companyId };
      if (isDeactivated !== undefined) body.isDeactivated = isDeactivated;
      if (defaultProcessId !== undefined) body.defaultProcessId = defaultProcessId;
      if (formLayoutId !== undefined) body.formLayoutId = formLayoutId;
      const res = await api.put<{ override: { isDeactivated: boolean } }>(
        `/document-types/global/${globalId}/override`,
        body,
      );
      return res.data.override;
    },
  });

  function handleToggleDeactivate(dt: DocumentType, isDeactivated: boolean) {
    if (!dt.companyId) return;
    overrideMutation.mutate(
      { globalId: dt.id, companyId: dt.companyId, isDeactivated },
      {
        onSuccess: () => {
          invalidate();
          toast.show({
            variant: 'success',
            title: isDeactivated
              ? t('tenant.documentTypes.toast.deactivated')
              : t('tenant.documentTypes.toast.activated'),
          });
          setPendingDeactivate(null);
        },
        onError: () => {
          toast.show({ variant: 'error', title: t('tenant.documentTypes.toast.overrideError') });
          setPendingDeactivate(null);
        },
      },
    );
  }

  function handleAssignProcess(dt: DocumentType, defaultProcessId: string | null) {
    if (!dt.companyId) return;
    overrideMutation.mutate(
      { globalId: dt.id, companyId: dt.companyId, defaultProcessId },
      {
        onSuccess: () => {
          invalidate();
          toast.show({
            variant: 'success',
            title: defaultProcessId
              ? t('tenant.documentTypes.toast.processAssigned')
              : t('tenant.documentTypes.toast.processCleared'),
          });
          setAssigningProcessTo(null);
        },
        onError: (err) => {
          const code = isAxiosError(err) ? (err.response?.data?.error as string | undefined) : null;
          toast.show({
            variant: 'error',
            title:
              defaultProcessErrorMessage(code, t) ??
              t('tenant.documentTypes.toast.assignProcessError'),
          });
        },
      },
    );
  }

  // T2.3 — przypisanie layoutu formularza na typie globalnym (nadpisanie per firma).
  function handleAssignLayout(dt: DocumentType, formLayoutId: string | null) {
    if (!dt.companyId) return;
    overrideMutation.mutate(
      { globalId: dt.id, companyId: dt.companyId, formLayoutId },
      {
        onSuccess: () => {
          invalidate();
          toast.show({
            variant: 'success',
            title: formLayoutId
              ? t('tenant.documentTypes.toast.layoutAssigned')
              : t('tenant.documentTypes.toast.layoutCleared'),
          });
          setAssigningLayoutTo(null);
        },
        onError: () => {
          toast.show({
            variant: 'error',
            title: t('tenant.documentTypes.toast.assignLayoutError'),
          });
        },
      },
    );
  }

  // Layout formularza typu globalnego przez inline cell (`LayoutPickerCell`) — symetrycznie
  // z `commitField`: silent success (tylko invalidate, bez toastu), reject z przetłumaczonym
  // komunikatem pokazywanym INLINE w popoverze. Celowo osobna od `handleAssignLayout` (ikonka
  // "Przypisz layout" + modal, bez własnego inline surface — tamten flow zostaje przy toaście).
  async function commitGlobalLayoutOverride(
    dt: DocumentType,
    formLayoutId: string | null,
  ): Promise<void> {
    if (!dt.companyId) return;
    try {
      await overrideMutation.mutateAsync({
        globalId: dt.id,
        companyId: dt.companyId,
        formLayoutId,
      });
      invalidate();
    } catch {
      throw new Error(t('tenant.documentTypes.toast.assignLayoutError'));
    }
  }

  // ─── Edycja inline (Nazwa/Kod/Kolejność/Format numeracji/Aktywny) — TYLKO wiersze
  // lokalne (patrz `isEditableLocal` w render). Jedna wspólna mutacja PUT (backend
  // przyjmuje częściowy payload); błąd mapowany na przetłumaczony string i rzucany jako
  // Error, żeby InlineEditableCell/NumberingCell zostawiły komórkę otwartą z komunikatem
  // (kontrakt onCommit: void | Promise<void>) — lustro `commitField` z
  // `AdminGlobalDocumentTypes.tsx`.
  const inlineUpdateMutation = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Record<string, unknown> }) => {
      const res = await api.put<{ documentType: DocumentTypeRecord }>(
        `/document-types/${id}`,
        patch,
      );
      return res.data.documentType;
    },
    onSuccess: () => {
      invalidate();
      toast.show({ variant: 'success', title: t('tenant.documentTypes.toast.updated') });
    },
  });

  function mapInlineErrorMessage(code: string | null | undefined): string {
    if (code === 'CODE_TAKEN') return t('tenant.documentTypes.toast.codeTaken');
    if (code === 'NAME_TAKEN') return t('tenant.documentTypes.toast.nameTaken');
    if (code === 'INVALID_NUMBERING_FORMAT' || code === 'NUMBERING_FORMAT_NO_COUNTER') {
      return t('tenant.documentTypes.toast.numberingFormatInvalid');
    }
    if (code === 'VALIDATION_ERROR') return t('tenant.documentTypes.toast.validationFailed');
    const processMessage = defaultProcessErrorMessage(code, t);
    if (processMessage !== null) return processMessage;
    return t('tenant.documentTypes.toast.updateError');
  }

  async function commitField(id: string, patch: Record<string, unknown>): Promise<void> {
    try {
      await inlineUpdateMutation.mutateAsync({ id, patch });
    } catch (err) {
      const code = isAxiosError(err) ? (err.response?.data?.error as string | undefined) : null;
      throw new Error(mapInlineErrorMessage(code), { cause: err });
    }
  }

  // Aktywny — checkbox inline (nie ma popovera do pokazania błędu inline, więc błąd
  // pokazujemy toastem; checkbox jest controlled przez `dt.isActive` z cache React Query,
  // więc przy błędzie (brak invalidate) sam „wraca" do poprzedniego stanu na re-renderze).
  function handleToggleActiveInline(dt: DocumentType, isActive: boolean) {
    commitField(dt.id, { isActive }).catch((err) => {
      toast.show({
        variant: 'error',
        title: err instanceof Error ? err.message : t('tenant.documentTypes.toast.updateError'),
      });
    });
  }

  // Sprawdź czy 404 (moduł nieaktywny)
  const isModuleInactive = isAxiosError(listQ.error) && listQ.error.response?.status === 404;

  const types = useMemo(() => listQ.data ?? [], [listQ.data]);

  // C2 — lista honoruje firmę aktywną z top-bara: przy konkretnej firmie (`scopedCompanyId`)
  // GET niesie `?companyId=`, więc wynik jest już zawężony do tej firmy. Tylko w trybie
  // "Wszystkie firmy" (`scopedCompanyId` = undefined) GET wraca bez `companyId` i jest to
  // nadal unia cross-company — typ globalny materializuje się per firma pod tym samym `id`,
  // więc sam `dt.id` NIE jest unikalny w wierszach. Klucz/testid zostaje kompozytowy
  // (id + companyId) niezależnie od trybu, wzorem `rowKey` w DocumentTypePickerModal.
  // Wiersz-relikt z `companyId: null` daje literalne "id:null" — bezpieczne, bo bez firmy
  // dany `id` (PK) występuje w unii co najwyżej raz (duplikacja per firma dotyczy tylko
  // wierszy z firmą).
  const rowKey = (dt: MergedDocumentTypeRow): string => `${dt.id}:${dt.companyId}`;

  // Etap 4 — kody aktywnych typów globalnych (opublikowane, niewycofane, wybieralne) dla
  // wybranej klasy. Używane w modalu do miękkiego ostrzeżenia o przesłonięciu typu systemowego.
  const activeGlobalCodes = types
    .filter((dt) => dt.origin === 'global' && dt.status === 'published' && dt.selectable)
    .map((dt) => dt.code);

  // Flagi wiersza — te same warunki co w dawnej ręcznej tabeli: edycja inline tylko dla
  // lokalnych, nieskasowanych; przypisanie procesu/layoutu także dla globalnych z firmą.
  const rowFlags = (dt: MergedDocumentTypeRow) => {
    const isDeleted = dt.gcrecord !== null;
    const isGlobal = dt.origin === 'global';
    const isEditableLocal = canManage && !isGlobal && !isDeleted;
    const canInlineAssign = isEditableLocal || (canManage && isGlobal && Boolean(dt.companyId));
    return { isDeleted, isGlobal, isEditableLocal, canInlineAssign };
  };

  // Opcje filtrów kolumn z listą wyboru — wartość = to, co zwraca `accessor` kolumny (lokalny
  // filtr tabeli porównuje wartość komórki z wybraną opcją). Proces i layout filtrujemy po ID
  // (nie po nazwie): typ z procesem spoza listy (np. zarchiwizowanym) albo podczas ładowania
  // listy NIE trafia pod „— brak —", a dwa procesy o tej samej nazwie są rozróżnialne.
  // „— brak —" = wspólny znacznik `NONE_FILTER_VALUE`, którego żaden UUID nie przyjmie.
  const layoutNoneLabel = t('tenant.recordTypes.formLayout.defaultOption');
  const yesLabel = t('common.yes');
  const noLabel = t('common.no');
  const originLabel = (origin: string) =>
    origin === 'global'
      ? t('tenant.documentTypes.list.filterOriginGlobal')
      : t('tenant.documentTypes.list.filterOriginLocal');
  const asOptions = (values: string[]) => values.map((v) => ({ value: v, label: v }));

  // Kolumny wspólnej tabeli (ConfigurableTable). Zawartość komórek 1:1 z dawnej ręcznej
  // tabeli (te same data-testid, edycja inline, przypisania). Filtry = wbudowany wiersz
  // filtrów tabeli — lokalnie, nad już pobraną listą klasy + firmy (bez nowych zapytań).
  const columns: ColumnDefinition<MergedDocumentTypeRow>[] = [
    {
      id: 'name',
      accessor: (dt) => dt.name,
      header: t('tenant.documentTypes.list.colName'),
      filterable: true,
      headerFilter: { category: 'text' },
      renderCell: (_v, dt) => {
        const { isEditableLocal } = rowFlags(dt);
        return (
          <>
            {isEditableLocal ? (
              <InlineEditableCell<string>
                value={dt.name}
                onCommit={(newVal) => commitField(dt.id, { name: newVal })}
                renderInput={(draft, onChange, onKeyDown, onBlur, ref, isCommitting) => (
                  <input
                    ref={ref as React.RefObject<HTMLInputElement>}
                    value={draft}
                    onChange={(e) => onChange(e.target.value)}
                    onKeyDown={onKeyDown}
                    onBlur={onBlur}
                    disabled={isCommitting}
                    className="w-full rounded border border-accent-500 bg-bg-primary px-2 py-0.5 text-sm font-medium text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-500"
                    data-testid={`document-type-name-input-${rowKey(dt)}`}
                  />
                )}
                renderDisplay={(v) => <div className="font-medium text-text-primary">{v}</div>}
                className="-mx-2 rounded px-2 py-1 hover:bg-bg-tertiary"
                data-testid={`document-type-name-${rowKey(dt)}`}
              />
            ) : (
              <div className="font-medium text-text-primary">{dt.name}</div>
            )}
          </>
        );
      },
    },
    {
      id: 'company',
      accessor: (dt) => companyLabel(dt.companyId),
      header: t('tenant.documentTypes.list.colCompany'),
      filterable: true,
      headerFilter: { category: 'text' },
      renderCell: (_v, dt) => {
        return <>{companyLabel(dt.companyId)}</>;
      },
    },
    {
      id: 'code',
      accessor: (dt) => dt.code,
      header: t('tenant.documentTypes.list.colCode'),
      filterable: true,
      headerFilter: { category: 'text' },
      renderCell: (_v, dt) => {
        const { isEditableLocal } = rowFlags(dt);
        return (
          <>
            {isEditableLocal ? (
              <InlineEditableCell<string>
                value={dt.code}
                onCommit={(newVal) => commitField(dt.id, { code: newVal })}
                renderInput={(draft, onChange, onKeyDown, onBlur, ref, isCommitting) => (
                  <input
                    ref={ref as React.RefObject<HTMLInputElement>}
                    value={draft}
                    onChange={(e) => onChange(e.target.value)}
                    onKeyDown={onKeyDown}
                    onBlur={onBlur}
                    disabled={isCommitting}
                    className="w-full rounded border border-accent-500 bg-bg-primary px-2 py-0.5 font-mono text-xs text-text-secondary focus:outline-none focus:ring-1 focus:ring-accent-500"
                    data-testid={`document-type-code-input-${rowKey(dt)}`}
                  />
                )}
                renderDisplay={(v) => (
                  <code className="rounded bg-bg-secondary px-1.5 py-0.5 text-xs text-text-secondary">
                    {v}
                  </code>
                )}
                className="-mx-2 rounded px-2 py-1 hover:bg-bg-tertiary"
                data-testid={`document-type-code-${rowKey(dt)}`}
              />
            ) : (
              <code className="rounded bg-bg-secondary px-1.5 py-0.5 text-xs text-text-secondary">
                {dt.code}
              </code>
            )}
          </>
        );
      },
    },
    {
      id: 'origin',
      accessor: (dt) => originLabel(dt.origin),
      header: t('tenant.documentTypes.list.colOrigin'),
      filterable: true,
      headerFilter: {
        category: 'select',
        options: asOptions([originLabel('local'), originLabel('global')]),
      },
      renderCell: (_v, dt) => {
        return (
          <>
            <div className="flex flex-wrap items-center gap-1">
              <OriginBadge origin={dt.origin} />
              {dt.shadowed && (
                <Badge color="amber" data-testid={`document-type-badge-shadowed-${rowKey(dt)}`}>
                  {t('tenant.documentTypes.badges.shadowed')}
                </Badge>
              )}
              {dt.deactivatedInCompany && (
                <Badge color="amber" data-testid={`document-type-badge-deactivated-${rowKey(dt)}`}>
                  {t('tenant.documentTypes.badges.deactivated')}
                </Badge>
              )}
            </div>
          </>
        );
      },
    },
    {
      id: 'numberingFormat',
      accessor: (dt) => dt.numberingFormat ?? '',
      header: t('tenant.documentTypes.list.colNumberingFormat'),
      filterable: true,
      headerFilter: { category: 'text' },
      renderCell: (_v, dt) => {
        const { isEditableLocal } = rowFlags(dt);
        return (
          <>
            {isEditableLocal ? (
              <NumberingCell
                code={dt.code}
                baseFieldOptions={numberingBaseFieldOptions}
                value={{
                  numberingFormat: dt.numberingFormat ?? '',
                  numberingBaseField: normalizeNumberingBaseSlug(
                    selectedClass,
                    dt.numberingBaseField,
                  ),
                  numberingReset: dt.numberingReset,
                }}
                onCommit={(next: NumberingCellValue) =>
                  commitField(dt.id, {
                    numberingFormat: next.numberingFormat.trim() || null,
                    numberingBaseField: next.numberingBaseField,
                    numberingReset: next.numberingReset,
                  })
                }
                data-testid={`document-type-numbering-${rowKey(dt)}`}
              />
            ) : (
              (dt.numberingFormat ?? <span className="text-text-muted">—</span>)
            )}
          </>
        );
      },
    },
    defaultProcessColumn<MergedDocumentTypeRow>({
      t,
      header: t('tenant.recordTypes.defaultProcess.column'),
      processes: processOptions,
      getProcessId: (dt) => dt.defaultProcessId,
      canEdit: (dt) => rowFlags(dt).canInlineAssign,
      isPending: (dt) =>
        (inlineUpdateMutation.isPending && inlineUpdateMutation.variables?.id === dt.id) ||
        (overrideMutation.isPending && overrideMutation.variables?.globalId === dt.id),
      onChange: (dt, nextId) => {
        if (rowFlags(dt).isGlobal) {
          handleAssignProcess(dt, nextId);
          return;
        }
        commitField(dt.id, { defaultProcessId: nextId }).catch((err) => {
          toast.show({
            variant: 'error',
            title: err instanceof Error ? err.message : t('tenant.documentTypes.toast.updateError'),
          });
        });
      },
      testId: (dt) => `document-type-process-select-${rowKey(dt)}`,
    }),
    formLayoutColumn<MergedDocumentTypeRow>({
      header: t('tenant.documentTypes.list.colFormLayout'),
      layouts: layoutOptions,
      noneLabel: layoutNoneLabel,
      getLayoutId: (dt) => dt.formLayoutId,
      canEdit: (dt) => rowFlags(dt).canInlineAssign,
      onCommit: (dt, nextId) =>
        rowFlags(dt).isGlobal
          ? commitGlobalLayoutOverride(dt, nextId)
          : commitField(dt.id, { formLayoutId: nextId }),
      testId: (dt) => `document-type-layout-${rowKey(dt)}`,
    }),
    {
      id: 'typeFields',
      accessor: (dt) => dt.id,
      header: t('tenant.documentTypes.fields.column'),
      sortable: false,
      filterable: false,
      renderCell: (_v, dt) => {
        const { isDeleted, isGlobal } = rowFlags(dt);
        // Pola dotyczą aktywnego typu w konkretnej firmie: typ skasowany, przesłonięty lokalną
        // kopią, niepublikowany typ globalny albo wiersz globalny bez firmy nie mają co ustawiać.
        const available =
          !isDeleted &&
          !dt.shadowed &&
          (!isGlobal || (dt.companyId !== null && dt.status === 'published'));
        if (!available) return <span className="text-text-muted">—</span>;
        const summary = summaryByPair.get(summaryPairKey(dt.id, dt.companyId));
        return (
          <Button
            variant="ghost"
            size="sm"
            className="-mx-2 h-auto min-h-8 whitespace-normal py-1 text-left"
            onClick={() => setFieldsPanelFor(dt)}
            data-testid={`document-type-fields-${rowKey(dt)}`}
          >
            {summaryQ.isLoading ? (
              <Skeleton
                as="span"
                width="5rem"
                height="0.875rem"
                data-testid={`document-type-fields-loading-${rowKey(dt)}`}
              />
            ) : summary ? (
              <>
                {t('tenant.documentTypes.fields.summary', {
                  fields: t('tenant.documentTypes.fields.fieldCount', {
                    count: summary.fieldCount,
                  }),
                  required: t('tenant.documentTypes.fields.requiredCount', {
                    count: summary.requiredCount,
                  }),
                })}
                {summary.deviationCount > 0 && (
                  <span
                    role="img"
                    aria-label={t('tenant.documentTypes.fields.deviationDotLabel', {
                      count: summary.deviationCount,
                    })}
                    title={t('tenant.documentTypes.fields.deviationDotLabel', {
                      count: summary.deviationCount,
                    })}
                    className="ml-1.5 inline-block h-2 w-2 rounded-full bg-[rgb(var(--color-warning))]"
                    data-testid={`document-type-fields-dot-${rowKey(dt)}`}
                  />
                )}
              </>
            ) : (
              t('tenant.documentTypes.fields.setFields')
            )}
            {/* Widoczny tekst jest nazwą przycisku; nazwa typu dochodzi tylko dla czytników ekranu. */}
            <span className="sr-only">
              {' '}
              {t('tenant.documentTypes.fields.openSr', { name: dt.name })}
            </span>
          </Button>
        );
      },
    },
    {
      id: 'sortOrder',
      accessor: (dt) => dt.sortOrder,
      header: t('tenant.documentTypes.list.colSort'),
      filterable: true,
      headerFilter: { category: 'number' },
      renderCell: (_v, dt) => {
        const { isEditableLocal } = rowFlags(dt);
        return (
          <>
            {isEditableLocal ? (
              <InlineEditableCell<number>
                value={dt.sortOrder}
                isEqual={(a, b) => Number(a) === Number(b)}
                onCommit={(newVal) => commitField(dt.id, { sortOrder: newVal })}
                renderInput={(draft, onChange, onKeyDown, onBlur, ref, isCommitting) => (
                  <input
                    ref={ref as React.RefObject<HTMLInputElement>}
                    type="number"
                    min={0}
                    value={draft}
                    onChange={(e) => {
                      const parsed = parseInt(e.target.value, 10);
                      onChange(Number.isNaN(parsed) ? 0 : Math.max(0, parsed));
                    }}
                    onKeyDown={onKeyDown}
                    onBlur={onBlur}
                    disabled={isCommitting}
                    className="w-20 rounded border border-accent-500 bg-bg-primary px-2 py-0.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-500"
                    data-testid={`document-type-sort-order-input-${rowKey(dt)}`}
                  />
                )}
                className="-mx-2 rounded px-2 py-1 hover:bg-bg-tertiary"
                data-testid={`document-type-sort-order-${rowKey(dt)}`}
              />
            ) : (
              dt.sortOrder
            )}
          </>
        );
      },
    },
    {
      id: 'isActive',
      accessor: (dt) => (dt.isActive ? yesLabel : noLabel),
      header: t('tenant.documentTypes.list.colActive'),
      filterable: true,
      headerFilter: { category: 'select', options: asOptions([yesLabel, noLabel]) },
      renderCell: (_v, dt) => {
        const { isEditableLocal } = rowFlags(dt);
        return (
          <>
            {isEditableLocal ? (
              <input
                type="checkbox"
                checked={dt.isActive}
                onChange={(e) => handleToggleActiveInline(dt, e.target.checked)}
                className="h-4 w-4 rounded border-border-primary"
                data-testid={`document-type-active-toggle-${rowKey(dt)}`}
              />
            ) : dt.isActive ? (
              <Badge color="accent">{t('tenant.documentTypes.list.active')}</Badge>
            ) : (
              <Badge color="gray">{t('tenant.documentTypes.list.inactive')}</Badge>
            )}
          </>
        );
      },
    },
    // „Wniosek o nieobecność" — tylko zakładki klas HR (patrz `showHrAbsenceColumn`). Zaznaczony
    // jest wiersz o `id` równym typowi z ustawienia, więc typ globalny świeci we wszystkich
    // swoich wierszach firm. Brak odczytu ustawienia → „—" (bez bannera nad tabelą).
    ...(showHrAbsenceColumn
      ? [
          {
            id: 'hrAbsence',
            accessor: (dt: MergedDocumentTypeRow) =>
              // Stan nieznany → wartość neutralna: wiersz nie pasuje ani do „Tak", ani do „Nie"
              // (komórki pokazują wtedy „—").
              !hrAbsence.isLoaded ? '' : dt.id === hrAbsence.typeId ? yesLabel : noLabel,
            header: t('tenant.documentTypes.hrAbsenceType.column'),
            filterable: true,
            headerFilter: { category: 'select' as const, options: asOptions([yesLabel, noLabel]) },
            renderCell: (_v: unknown, dt: MergedDocumentTypeRow) => {
              if (!hrAbsence.isLoaded) return <span className="text-text-muted">—</span>;
              const { isDeleted, isGlobal } = rowFlags(dt);
              const checkbox = (
                <Checkbox
                  checked={dt.id === hrAbsence.typeId}
                  onChange={(e) => hrAbsence.setAbsenceType(dt.id, dt.name, e.target.checked)}
                  disabled={!hrAbsence.canManage || hrAbsence.isPending || isDeleted}
                  // Podpowiedź „dla wszystkich firm" opakowuje niefokusowalny span — czytnik ekranu
                  // i klawiatura jej nie dostaną, więc wiersz globalny niesie to w etykiecie pola.
                  aria-label={t(
                    isGlobal
                      ? 'tenant.documentTypes.hrAbsenceType.checkboxLabelGlobal'
                      : 'tenant.documentTypes.hrAbsenceType.checkboxLabel',
                    { name: dt.name },
                  )}
                  data-testid={`document-type-absence-toggle-${rowKey(dt)}`}
                />
              );
              return isGlobal ? (
                <Tooltip content={t('tenant.documentTypes.hrAbsenceType.globalTooltip')}>
                  <span className="inline-flex">{checkbox}</span>
                </Tooltip>
              ) : (
                checkbox
              );
            },
          } satisfies ColumnDefinition<MergedDocumentTypeRow>,
        ]
      : []),
  ];

  const renderRowActions = (dt: MergedDocumentTypeRow) => {
    const { isDeleted, isGlobal } = rowFlags(dt);
    return (
      <>
        {isGlobal ? (
          canManage && dt.companyId ? (
            <div className="flex items-center justify-end gap-1">
              <button
                type="button"
                onClick={() => setCopyingFrom(dt)}
                aria-label={t('tenant.documentTypes.actions.createLocalCopy')}
                title={t('tenant.documentTypes.actions.createLocalCopy')}
                className="rounded p-1.5 text-text-muted transition hover:bg-bg-tertiary hover:text-text-primary"
                data-testid={`document-type-copy-${rowKey(dt)}`}
              >
                <Copy className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setAssigningProcessTo(dt)}
                aria-label={t('tenant.documentTypes.actions.assignProcess')}
                title={t('tenant.documentTypes.actions.assignProcess')}
                className="rounded p-1.5 text-text-muted transition hover:bg-bg-tertiary hover:text-text-primary"
                data-testid={`document-type-assign-process-${rowKey(dt)}`}
              >
                <Workflow className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setAssigningLayoutTo(dt)}
                aria-label={t('tenant.documentTypes.actions.assignLayout')}
                title={t('tenant.documentTypes.actions.assignLayout')}
                className="rounded p-1.5 text-text-muted transition hover:bg-bg-tertiary hover:text-text-primary"
                data-testid={`document-type-assign-layout-${rowKey(dt)}`}
              >
                <LayoutTemplate className="h-3.5 w-3.5" />
              </button>
              {dt.deactivatedInCompany ? (
                <button
                  type="button"
                  onClick={() => handleToggleDeactivate(dt, false)}
                  aria-label={t('tenant.documentTypes.actions.activateInCompany')}
                  title={t('tenant.documentTypes.actions.activateInCompany')}
                  className="rounded p-1.5 text-text-muted transition hover:bg-bg-tertiary hover:text-text-primary"
                  data-testid={`document-type-activate-${rowKey(dt)}`}
                >
                  <Eye className="h-3.5 w-3.5" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setPendingDeactivate(dt)}
                  aria-label={t('tenant.documentTypes.actions.deactivateInCompany')}
                  title={t('tenant.documentTypes.actions.deactivateInCompany')}
                  className="rounded p-1.5 text-text-muted transition hover:bg-bg-tertiary hover:text-text-primary"
                  data-testid={`document-type-deactivate-${rowKey(dt)}`}
                >
                  <EyeOff className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-end text-text-muted">—</div>
          )
        ) : (
          canManage && (
            <div className="flex items-center justify-end gap-1">
              {isDeleted ? (
                <button
                  type="button"
                  onClick={() => setPendingRestore(dt)}
                  aria-label={t('tenant.documentTypes.confirmRestore.title')}
                  className="rounded p-1.5 text-text-muted transition hover:bg-bg-tertiary hover:text-text-primary"
                  data-testid={`document-type-restore-${rowKey(dt)}`}
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setEditing(dt)}
                    aria-label={t('common.edit')}
                    className="rounded p-1.5 text-text-muted transition hover:bg-bg-tertiary hover:text-text-primary"
                    data-testid={`document-type-edit-${rowKey(dt)}`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setPendingDelete(dt)}
                    aria-label={t('common.delete')}
                    className="rounded p-1.5 text-text-muted transition hover:bg-error-500/10 hover:text-error-500"
                    data-testid={`document-type-delete-${rowKey(dt)}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
            </div>
          )
        )}
      </>
    );
  };

  return (
    <div data-testid="document-types-tab">
      {/* Nagłówek sekcji: tytuł „Typy dokumentów" rysuje już ekran Ustawień — tu tylko opis
          i akcja (wcześniej tytuł był zdublowany). */}
      <header className="mb-4 flex items-center justify-between gap-3">
        <p className="text-sm text-text-secondary">{t('tenant.documentTypes.list.subtitle')}</p>
        {canManage && (
          <Button
            variant="primary"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => setCreateOpen(true)}
            data-testid="document-type-new-btn"
          >
            {t('tenant.documentTypes.list.newType')}
          </Button>
        )}
      </header>

      {/* Klasy dokumentów = zakładki z podkreśleniem (Figma 08.4 „Tabs · Underline" — sekcje
          na stronie; przełącznik segmentowy jest w systemie tylko do zmiany widoku). */}
      <Tabs
        value={selectedClass}
        onChange={(cls) => {
          setSelectedClass(cls as DocumentClass);
          setShowDeleted(false);
          setFieldsPanelFor(null);
        }}
        idPrefix="document-types-class"
        className="mb-4"
        items={ALL_CLASSES.map((cls) => ({
          value: cls,
          label: t(`tenant.documentTypes.classes.${cls}`),
          testId: `document-types-class-${cls}`,
          content: null,
        }))}
      />

      {/* Toggle „pokaż skasowane" — widoczny tylko dla manage */}
      {canManage && (
        <div className="mb-3 flex justify-end">
          <button
            type="button"
            onClick={() => setShowDeleted((v) => !v)}
            data-testid="document-types-show-deleted"
            className={
              'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition ' +
              (showDeleted
                ? 'border-accent-500 bg-accent-500/10 text-accent-500'
                : 'border-border-primary bg-bg-primary text-text-secondary hover:border-accent-500 hover:text-accent-500')
            }
          >
            {showDeleted ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            {t('tenant.documentTypes.showDeleted')}
          </button>
        </div>
      )}

      {/* Treść */}
      {listQ.isLoading ? (
        <div className="flex items-center justify-center py-10">
          <Spinner />
        </div>
      ) : isModuleInactive ? (
        <Card padding="md">
          <p className="text-sm text-text-tertiary">
            {t('tenant.documentTypes.list.moduleInactive')}
          </p>
        </Card>
      ) : !canView ? (
        <Card padding="md">
          <p className="text-sm text-text-tertiary">
            {t('tenant.documentTypes.list.noPermission')}
          </p>
        </Card>
      ) : types.length === 0 ? (
        <Card padding="md">
          <p className="text-sm text-text-tertiary">{t('tenant.documentTypes.list.empty')}</p>
        </Card>
      ) : (
        <ConfigurableTable<MergedDocumentTypeRow>
          // key = klasa: przełączenie zakładki montuje tabelę od nowa (czyści filtry kolumn,
          // tak jak dawny reset filtrów przy zmianie klasy).
          key={selectedClass}
          tableId={`tenant-document-types-${selectedClass}`}
          data={types}
          getRowId={rowKey}
          columns={columns}
          filterable
          configurable={false}
          persistConfig={false}
          showViewModeToggle={false}
          stickyHeader
          testIdPrefix="document-type"
          rowClassName={(dt) =>
            dt.gcrecord !== null || dt.shadowed || dt.deactivatedInCompany ? 'opacity-50' : ''
          }
          emptyTitle={t('tenant.documentTypes.list.noFilterResults')}
          renderActions={renderRowActions}
          actionsColumnWidth={160}
        />
      )}

      {/* Modal create / edit */}
      <DocumentTypeFormModal
        open={createOpen || editing !== null}
        mode={editing ? 'edit' : 'create'}
        documentClass={selectedClass}
        baseFieldOptions={numberingBaseFieldOptions}
        initialValues={editModalInitialValues}
        processes={processOptions}
        processesLoading={processesLoading}
        companies={companies}
        globalCodes={activeGlobalCodes}
        layouts={layoutOptions}
        layoutsUnavailable={layoutsUnavailable}
        saving={createMutation.isPending || updateMutation.isPending}
        onSubmit={(values) => {
          if (editing) {
            updateMutation.mutate({ id: editing.id, values, original: editing });
          } else {
            createMutation.mutate(values);
          }
        }}
        onClose={() => {
          setCreateOpen(false);
          setEditing(null);
        }}
      />

      {/* Potwierdzenie usunięcia */}
      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('tenant.documentTypes.confirmDelete.title')}
        message={
          pendingDelete
            ? [
                t('tenant.documentTypes.confirmDelete.message', { name: pendingDelete.name }),
                // Usunięcie typu będącego wnioskiem o nieobecność zostawia klienta bez takiego typu.
                // Ostrzeżenie widzą tylko osoby, które mogą odczytać to ustawienie (zapytanie idzie
                // tylko przy widocznej kolumnie; serwer zwraca 403 bez praw do obu klas HR). Pozostali
                // nie wiedzą, który typ jest wnioskiem o nieobecność — o dostępie decyduje serwer.
                hrAbsence.isLoaded && pendingDelete.id === hrAbsence.typeId
                  ? t('tenant.documentTypes.hrAbsenceType.deleteNote')
                  : null,
              ]
                .filter((part): part is string => part !== null)
                .join(' ')
            : ''
        }
        confirmLabel={t('common.delete')}
        variant="danger"
        onConfirm={() => {
          if (pendingDelete) deleteMutation.mutate(pendingDelete.id);
        }}
        onClose={() => setPendingDelete(null)}
      />

      {/* Potwierdzenie przywrócenia */}
      <ConfirmDialog
        open={pendingRestore !== null}
        title={t('tenant.documentTypes.confirmRestore.title')}
        message={
          pendingRestore
            ? t('tenant.documentTypes.confirmRestore.message', { name: pendingRestore.name })
            : ''
        }
        confirmLabel={t('tenant.documentTypes.confirmRestore.confirm')}
        variant="primary"
        onConfirm={() => {
          if (pendingRestore) restoreMutation.mutate(pendingRestore.id);
        }}
        onClose={() => setPendingRestore(null)}
      />

      {/* Etap 4 — „Utwórz kopię lokalną" z typu globalnego */}
      <DocumentTypeFormModal
        open={copyingFrom !== null}
        mode="copy"
        documentClass={selectedClass}
        baseFieldOptions={numberingBaseFieldOptions}
        initialValues={copyModalInitialValues}
        processes={processOptions}
        processesLoading={processesLoading}
        companies={companies}
        globalCodes={activeGlobalCodes}
        layouts={layoutOptions}
        layoutsUnavailable={layoutsUnavailable}
        saving={copyMutation.isPending}
        onSubmit={(values) => {
          if (copyingFrom) copyMutation.mutate({ globalId: copyingFrom.id, values });
        }}
        onClose={() => setCopyingFrom(null)}
      />

      {/* Etap 4 — potwierdzenie deaktywacji typu globalnego w tej firmie (odwracalne — primary) */}
      <ConfirmDialog
        open={pendingDeactivate !== null}
        title={t('tenant.documentTypes.confirmDeactivate.title')}
        message={
          pendingDeactivate
            ? t('tenant.documentTypes.confirmDeactivate.message', { name: pendingDeactivate.name })
            : ''
        }
        confirmLabel={t('tenant.documentTypes.confirmDeactivate.confirm')}
        variant="primary"
        onConfirm={() => {
          if (pendingDeactivate) handleToggleDeactivate(pendingDeactivate, true);
        }}
        onClose={() => setPendingDeactivate(null)}
      />

      {/* Etap 4 — przypisanie domyślnego procesu na typie globalnym (nadpisanie per firma) */}
      <AssignProcessModal
        open={assigningProcessTo !== null}
        processes={processOptions}
        currentProcessId={assigningProcessTo?.defaultProcessId ?? null}
        submitting={overrideMutation.isPending}
        onConfirm={(processId) => {
          if (assigningProcessTo) handleAssignProcess(assigningProcessTo, processId);
        }}
        onClose={() => setAssigningProcessTo(null)}
      />

      {/* Panel boczny „Pola typu” (pola dodatkowe i wymagalność per typ, nakładka firmy) */}
      <DocumentTypeFieldsDrawer
        open={fieldsPanelFor !== null}
        onClose={() => setFieldsPanelFor(null)}
        docClass={selectedClass}
        typeId={fieldsPanelFor?.id ?? null}
        typeName={fieldsPanelFor?.name ?? ''}
        companyId={fieldsPanelFor?.companyId ?? null}
        companyName={fieldsPanelFor ? companyLabel(fieldsPanelFor.companyId) : ''}
        canManage={canManage}
      />

      {/* Etap 2 (T2.3) — przypisanie layoutu formularza na typie globalnym (nadpisanie per firma) */}
      <AssignLayoutModal
        open={assigningLayoutTo !== null}
        layouts={layoutOptions}
        currentLayoutId={assigningLayoutTo?.formLayoutId ?? null}
        submitting={overrideMutation.isPending}
        onConfirm={(formLayoutId) => {
          if (assigningLayoutTo) handleAssignLayout(assigningLayoutTo, formLayoutId);
        }}
        onClose={() => setAssigningLayoutTo(null)}
      />
    </div>
  );
}
